import React, { useCallback, useEffect, useRef, useState } from 'react'
import { colors, DragHorizontalIcon } from '@diversioteam/diversio-ds'
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core'
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { nanoid } from 'nanoid'
import throttle from 'lodash/throttle'

import { useUpdatePulseSurvey } from 'hooks/pulseSurveys/useUpdatePulseSurvey'
import { useDebounce } from 'hooks/useDebounce'
import {
    PulseSurveyDisplaySubType,
    PulseSurveyQuestionType,
    PulseSurveyWeight,
} from 'api/actions/pulseSurveys/pulseSurveysActions.types'

import { MultipleChoice } from './Fields/MultipleChoice'
import { Header } from './Header'
import { FormBuilderProps, FormElement, FormElementOption } from './formBuilder.types'
import { SortableItem } from './SortableItem'
import { Checkbox } from './Fields/Checkbox'
import { Paragraph } from './Fields/Paragraph'
import { ShortAnswer } from './Fields/ShortAnswer'
import { Rating } from './Fields/Rating'
import {
    DEFAULT_OPTION,
    DEFAULT_RATING_TYPE,
    DEFAULT_RATING_WEIGHT,
    isCheckbox,
    isMultipleChoice,
    isRating,
} from './formBuilder.helpers'
import * as S from './formBuilder.styles'

export const FormBuilder = ({ surveyUUID, defaultData, previewMode = false }: FormBuilderProps) => {
    const mutation = useUpdatePulseSurvey()

    const initialData =
        defaultData.questions.length > 0
            ? defaultData.questions
            : [
                  {
                      id: nanoid(),
                      value: 'Question',
                      type: 'multiple_choice' as const,
                      required: false,
                      options: [
                          {
                              id: nanoid(),
                              value: 'Option',
                          },
                      ],
                      hasOther: false,
                  },
              ]

    const [title, setTitle] = useState(defaultData.title)
    const [description, setDescription] = useState(defaultData.description)

    const debouncedTitle = useDebounce(title, 500)

    const [data, setData] = useState<FormElement[]>(initialData)
    const [activeElementId, setActiveElementId] = useState(initialData[0].id)

    const isDeleteElementButtonDisabled = data.length === 1

    const throttledMutation = useCallback(
        throttle((updatedData) => {
            mutation.mutate(updatedData)
        }, 1000),
        [],
    )

    const isInitialRender = useRef(true)
    const isTitleUpdatedByEffect = useRef(false)

    const updateData = () => {
        const updatedData = {
            surveyUUID,
            body: {
                title: debouncedTitle,
                description,
                config_data: {
                    display_name: title,
                    questions: data.map(({ value, type, options, hasOther, required, ratingType, ratingWeight }) => {
                        return {
                            title: value,
                            question_type: type,
                            options: options?.map(({ value }) => value),
                            has_other: hasOther,
                            required,
                            display_type: ratingType ? 'emoji' : undefined,
                            display_sub_type: ratingType,
                            weight: ratingWeight,
                        }
                    }),
                },
            },
        }

        throttledMutation(updatedData)
    }

    useEffect(() => {
        if (defaultData.title !== title) {
            isTitleUpdatedByEffect.current = true

            setTitle(defaultData.title)
        }
    }, [defaultData.title])

    useEffect(() => {
        if (previewMode) {
            return
        }

        if (isInitialRender.current) {
            isInitialRender.current = false

            return
        }

        if (isTitleUpdatedByEffect.current) {
            isTitleUpdatedByEffect.current = false

            return
        }

        updateData()
    }, [JSON.stringify(data), debouncedTitle, description, throttledMutation])

    const handleChangeTitle = (title: string) => {
        setTitle(title)
    }

    const handleChangeDescription = (description: string) => {
        setDescription(description)
    }

    const handleAddElement = () => {
        const newElement = {
            id: nanoid(),
            value: 'Question',
            type: 'multiple_choice' as const,
            required: false,
            options: DEFAULT_OPTION,
            hasOther: false,
        }

        setData((prevState) => [...prevState, newElement])
        setActiveElementId(newElement.id)
    }

    const handleDeleteElement = (id: string) => {
        const newData = data.filter((val) => val.id !== id)

        setData(newData)

        if (newData.length > 0) {
            const previousElementIndex = data.findIndex((element) => element.id === id) - 1
            const newActiveElementId = newData[Math.max(previousElementIndex, 0)].id

            setActiveElementId(newActiveElementId)
        }
    }

    const handleAddAfter = (data: FormElement[], index: number, element: FormElement) => {
        return [...data.slice(0, index + 1), element, ...data.slice(index + 1)]
    }

    const handleDuplicateElement = (id: string) => {
        const targetElement = data.find((element) => element.id === id)
        const targetElementIndex = data.findIndex((element) => element.id === id)

        if (!targetElement) {
            return
        }

        const newElement = {
            ...targetElement,
            id: nanoid(),
            options: targetElement.options?.map((option) => ({
                ...option,
                id: nanoid(),
            })),
        }

        const newArr = handleAddAfter(data, targetElementIndex, newElement)

        setData(newArr)
        setActiveElementId(newElement.id)
    }

    const handleChangeElementValue = (id: string, value: string) => {
        const newData = data.map((element) => {
            if (element.id === id) {
                return { ...element, value }
            } else {
                return element
            }
        })

        setData(newData)
    }

    const handleToggleRequired = (id: string) => {
        const newData = data.map((element) => {
            if (element.id === id) {
                return { ...element, required: !element.required }
            } else {
                return element
            }
        })
        setData(newData)
    }

    const handleChangeElementType = (id: string, type: PulseSurveyQuestionType) => {
        const newData = data.map((element) => {
            if (element.id === id) {
                const updates: Partial<FormElement> = { type }

                if (type === 'multiple_choice' || type === 'checkbox') {
                    updates.hasOther = false
                    updates.options = DEFAULT_OPTION
                } else {
                    updates.hasOther = undefined
                    updates.options = undefined
                }

                if (type === 'rating') {
                    updates.ratingType = DEFAULT_RATING_TYPE
                    updates.ratingWeight = DEFAULT_RATING_WEIGHT
                } else {
                    updates.ratingType = undefined
                    updates.ratingWeight = undefined
                }

                return { ...element, ...updates }
            } else {
                return element
            }
        })

        setData(newData)
    }

    const handleChangeRatingType = (id: string, ratingType: PulseSurveyDisplaySubType) => {
        const newData = data.map((element) => {
            if (element.id === id) {
                return { ...element, ratingType }
            } else {
                return element
            }
        })

        setData(newData)
    }

    const handleChangeRatingWeight = (id: string, ratingWeight: PulseSurveyWeight) => {
        const newData = data.map((element) => {
            if (element.id === id) {
                return { ...element, ratingWeight }
            } else {
                return element
            }
        })

        setData(newData)
    }

    const handleAddOption = (id: string, option: FormElementOption) => {
        const newData = data.map((element) => {
            if (element.id === id) {
                const objVal = element?.options || []

                return { ...element, options: [...objVal, option] }
            } else {
                return element
            }
        })
        setData(newData)
    }

    const handleChangeOptionValue = (elementId: string, optionId: string, value: string) => {
        const newData = data.map((element) => {
            if (element.id === elementId) {
                element?.options?.map((option) => {
                    if (option.id === optionId) {
                        option.value = value
                    }
                })

                return element
            } else {
                return element
            }
        })

        setData(newData)
    }

    const handleDeleteOption = (elementId: string, optionId: string) => {
        const newData = data.map((element) => {
            if (element.id === elementId) {
                const newOptions = element?.options?.filter((option) => option.id !== optionId)

                return { ...element, options: newOptions }
            } else {
                return element
            }
        })

        setData(newData)
    }

    const handleReorderOptions = (elementId: string, reorderedOptions: FormElementOption[]) => {
        const newData = data.map((element) => {
            if (element.id === elementId) {
                return { ...element, options: reorderedOptions }
            } else {
                return element
            }
        })

        setData(newData)
    }

    const handleToggleOtherOption = (elementId: string, hasOther: boolean) => {
        const newData = data.map((element) => {
            if (element.id === elementId) {
                return { ...element, hasOther }
            } else {
                return element
            }
        })

        setData(newData)
    }

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event

        if (!over) {
            return
        }

        if (active.id !== over.id) {
            const oldIndex = data.findIndex((item) => item.id === active.id)
            const newIndex = data.findIndex((item) => item.id === over.id)

            const reorderedItems = arrayMove(data, oldIndex, newIndex)

            setData(reorderedItems)
        }
    }

    const handleChangeActiveElement = (id: string) => {
        setActiveElementId(id)
    }

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
    )

    const sharedProps = {
        activeElementId,
        isDeleteElementButtonDisabled,
        onAddElement: handleAddElement,
        onChangeElementValue: handleChangeElementValue,
        onDeleteElement: handleDeleteElement,
        onToggleRequired: handleToggleRequired,
        onChangeElementType: handleChangeElementType,
        onDuplicateElement: handleDuplicateElement,
        onChangeActiveElement: handleChangeActiveElement,
        previewMode,
    }

    const renderElements = ({ item }: { item: FormElement }) => {
        const isActive = activeElementId === item.id

        switch (item.type) {
            case 'multiple_choice':
                if (isMultipleChoice(item)) {
                    return (
                        <MultipleChoice
                            {...sharedProps}
                            key={item.id}
                            item={item}
                            isActive={isActive}
                            onAddOption={handleAddOption}
                            onChangeOptionValue={handleChangeOptionValue}
                            onDeleteOption={handleDeleteOption}
                            onReorderOptions={handleReorderOptions}
                            onToggleOtherOption={handleToggleOtherOption}
                        />
                    )
                }
                break
            case 'checkbox':
                if (isCheckbox(item)) {
                    return (
                        <Checkbox
                            {...sharedProps}
                            key={item.id}
                            item={item}
                            isActive={isActive}
                            onAddOption={handleAddOption}
                            onChangeOptionValue={handleChangeOptionValue}
                            onDeleteOption={handleDeleteOption}
                            onReorderOptions={handleReorderOptions}
                            onToggleOtherOption={handleToggleOtherOption}
                        />
                    )
                }
                break
            case 'paragraph':
                return <Paragraph {...sharedProps} key={item.id} item={item} isActive={isActive} />
            case 'short_answer':
                return <ShortAnswer {...sharedProps} key={item.id} item={item} isActive={isActive} />
            case 'rating':
                if (isRating(item)) {
                    return (
                        <Rating
                            {...sharedProps}
                            key={item.id}
                            item={item}
                            isActive={isActive}
                            onChangeRatingType={handleChangeRatingType}
                            onChangeRatingWeight={handleChangeRatingWeight}
                        />
                    )
                }
                break
            default:
                return <></>
        }
    }

    if (previewMode) {
        return <>{data.map((item) => renderElements({ item }))}</>
    }

    return (
        <S.FormBuilderWrapper>
            <Header
                title={title}
                onChangeTitle={handleChangeTitle}
                description={description}
                onChangeDescription={handleChangeDescription}
            />

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={data} strategy={verticalListSortingStrategy}>
                    {data.map((item) => (
                        <SortableItem
                            key={item.id}
                            id={item.id}
                            dragHandle={
                                <S.DragArea>
                                    <DragHorizontalIcon type="line" color={colors.primary40} />
                                </S.DragArea>
                            }
                        >
                            {renderElements({ item })}
                        </SortableItem>
                    ))}
                </SortableContext>
            </DndContext>
        </S.FormBuilderWrapper>
    )
}
